import { QUIZ_ROUTES } from "./quizRoutes";
const QUIZ_FLOW_STORAGE_KEY = "vidya-kasam:quiz-instruction-flow";

function toPositiveNumber(value, fallback = 0) {
  const nextValue = Number(value);
  return Number.isFinite(nextValue) && nextValue > 0 ? nextValue : fallback;
}

function cloneIfObject(value) {
  return value && typeof value === "object" ? { ...value } : null;
}

export function getStoredQuizFlowState() {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const rawValue = window.sessionStorage.getItem(QUIZ_FLOW_STORAGE_KEY);
    return rawValue ? JSON.parse(rawValue) : {};
  } catch (_error) {
    return {};
  }
}

export function buildQuizFlowState(routeState = {}, fallbackState = {}) {
  routeState = routeState || {};
  // Explicit navigation owns its context; another screen's cached state is only
  // a fallback for a refresh with no route state.
  if (Object.keys(routeState).length) fallbackState = {};
  const mergedState = {
    ...fallbackState,
    ...routeState,
  };
  const quizData = cloneIfObject(mergedState?.quizData) || {};
  const selectedQuiz =
    cloneIfObject(mergedState?.selectedQuiz) ||
    (Object.keys(quizData).length
      ? {
          ...quizData,
          id: Number(quizData?.quizId || quizData?.quiz_id || quizData?.id || 0),
          quizId: Number(quizData?.quizId || quizData?.quiz_id || quizData?.id || 0),
          quiz_id: Number(quizData?.quizId || quizData?.quiz_id || quizData?.id || 0),
          title: quizData?.quizName || quizData?.title || "",
          quizName: quizData?.quizName || quizData?.title || "",
        }
      : null);
  const stateId = toPositiveNumber(
    mergedState?.selectedState?.state_id ||
      mergedState?.state_id ||
      quizData?.statesId ||
      quizData?.states_id ||
      fallbackState?.selectedState?.state_id ||
      fallbackState?.state_id,
    13
  );
  const categoryId = toPositiveNumber(
    routeState?.category_id ||
      routeState?.quizRequestParams?.category_id ||
      routeState?.selectedQuizOptionData?.category_id ||
      mergedState?.category_id ||
      quizData?.categoryId ||
      quizData?.category_id ||
      fallbackState?.category_id,
    1
  );
  const routeKey = toPositiveNumber(
    routeState?.route_key ||
      routeState?.quizRequestParams?.route_key ||
      routeState?.selectedQuizOptionData?.routeKey ||
      routeState?.selectedQuizOptionData?.route_key ||
      mergedState?.route_key ||
      mergedState?.quizRequestParams?.route_key ||
      fallbackState?.route_key,
    0
  );
  const quizId = toPositiveNumber(
    selectedQuiz?.quizId || selectedQuiz?.quiz_id || selectedQuiz?.id || quizData?.quizId || quizData?.quiz_id || quizData?.id,
    0
  );
  const assesmentBatchId = toPositiveNumber(
    mergedState?.assesment_batch_id || mergedState?.selectedAttempt?.assesment_batch_id,
    0
  );
  const selectedState = cloneIfObject(mergedState?.selectedState) || {
    state_id: stateId,
    state_name:
      mergedState?.stateName ||
      quizData?.stateName ||
      quizData?.state_name ||
      "",
  };
  const normalizedQuizData = {
    ...quizData,
    quizId,
    quiz_id: quizId,
    id: quizId,
    quizName: quizData?.quizName || quizData?.title || selectedQuiz?.quizName || selectedQuiz?.title || "",
    title: quizData?.quizName || quizData?.title || selectedQuiz?.quizName || selectedQuiz?.title || "",
    statesId: stateId,
    categoryId,
    stateName:
      mergedState?.stateName ||
      quizData?.stateName ||
      quizData?.state_name ||
      selectedState?.state_name ||
      "",
    categoryName:
      mergedState?.selectedQuizOption || quizData?.categoryName || quizData?.category_name || "Tests & Quizzes",
    bannerImage: mergedState?.listBannerImage || quizData?.bannerImage || quizData?.image || "",
  };
  const selectedMode = cloneIfObject(mergedState?.selectedMode);
  const selectedAttempt = cloneIfObject(mergedState?.selectedAttempt);
  const quizSubmission = cloneIfObject(mergedState?.quiz_submission);
  const selectedLang = String(
    mergedState?.selected_lang ||
      selectedMode?.langShortName ||
      normalizedQuizData?.selected_lang ||
      "EN"
  )
    .trim()
    .toUpperCase() || "EN";
  const quizBackTarget = mergedState?.quizBackTarget
    ? {
        ...mergedState.quizBackTarget,
        pathname: mergedState.quizBackTarget.pathname,
        state: cloneIfObject(mergedState.quizBackTarget.state) || {},
      }
    : null;

  return {
    ...fallbackState,
    ...routeState,
    quizData: normalizedQuizData,
    selectedQuiz,
    selectedState,
    selectedMode,
    selectedAttempt,
    quiz_submission: quizSubmission,
    state_id: stateId,
    stateName: normalizedQuizData.stateName,
    category_id: categoryId,
    route_key: routeKey,
    selectedQuizOption: normalizedQuizData.categoryName,
    listBannerImage: normalizedQuizData.bannerImage,
    quiz_id: quizId,
    assesment_batch_id: assesmentBatchId,
    selected_lang: selectedLang,
    resume_on_open: Boolean(mergedState?.resume_on_open),
    quizBackTarget,
  };
}

export function persistQuizFlowState(flowState) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(
      QUIZ_FLOW_STORAGE_KEY,
      JSON.stringify(flowState || {})
    );
  } catch (_error) {
    // Ignore storage failures and fall back to route state.
  }
}

export function buildQuizBackTarget(pathname, state = {}) {
  const history = typeof window !== 'undefined' && window.location.pathname === pathname ? window.history.state : null;
  const savedHistory = pathname === QUIZ_ROUTES.start ? state.quizStartHistory : null;
  return {
    pathname,
    state: buildQuizFlowState(state),
    ...(Number.isInteger(history?.idx) ? { historyIndex: history.idx, historyKey: history.key } : savedHistory || {}),
  };
}

export function buildQuizListState(flowState = {}) {
  const resolvedFlowState = buildQuizFlowState(flowState);

  return {
    series_id: resolvedFlowState.series_id,
    selectedSeries: resolvedFlowState.selectedSeries,
    seriesName: resolvedFlowState.seriesName,
    quizListPath: resolvedFlowState.quizListPath,
    quizListQueryState: resolvedFlowState.quizListQueryState,
    seriesListQueryState: resolvedFlowState.seriesListQueryState,
    quizBackTarget: resolvedFlowState.quizListBackTarget?.state?.quizBackTarget || null,
    selectedState: resolvedFlowState.selectedState || {
      state_id: resolvedFlowState.state_id,
      state_name: resolvedFlowState.stateName,
    },
    state_id: resolvedFlowState.state_id,
    state_name: resolvedFlowState.stateName,
    stateName: resolvedFlowState.stateName,
    category_id: resolvedFlowState.category_id,
    route_key: resolvedFlowState.route_key,
    selectedQuizOption: resolvedFlowState.selectedQuizOption,
    listBannerImage: resolvedFlowState.listBannerImage,
  };
}

export function buildStartQuizState(flowState = {}) {
  const resolvedFlowState = buildQuizFlowState(flowState);
  const listState = buildQuizListState(resolvedFlowState);
  let ancestor = resolvedFlowState.quizBackTarget;
  const seen = new Set();
  while (ancestor?.pathname && ancestor.pathname !== QUIZ_ROUTES.start && !seen.has(ancestor)) {
    seen.add(ancestor);
    ancestor = ancestor.state?.quizBackTarget;
  }
  const currentHistory = typeof window !== 'undefined' && window.location.pathname === QUIZ_ROUTES.start ? window.history.state : null;
  const quizStartHistory = Number.isInteger(currentHistory?.idx) ? { historyIndex: currentHistory.idx, historyKey: currentHistory.key }
    : Number.isInteger(ancestor?.historyIndex) ? { historyIndex: ancestor.historyIndex, historyKey: ancestor.historyKey } : resolvedFlowState.quizStartHistory;

  return buildQuizFlowState({
    ...resolvedFlowState,
    ...listState,
    quizStartHistory,
    selectedQuiz:
      resolvedFlowState.selectedQuiz ||
      (resolvedFlowState.quizData?.quizId
        ? {
            ...resolvedFlowState.quizData,
            id: resolvedFlowState.quizData.quizId,
            quizId: resolvedFlowState.quizData.quizId,
            quiz_id: resolvedFlowState.quizData.quizId,
            title: resolvedFlowState.quizData.quizName || resolvedFlowState.quizData.title || "",
            quizName: resolvedFlowState.quizData.quizName || resolvedFlowState.quizData.title || "",
          }
        : null),
    selectedAttempt: null,
    selectedMode: null,
    assesment_batch_id: 0,
    resume_on_open: false,
    quiz_submission: null,
    quizBackTarget: findQuizListBackTarget(resolvedFlowState) || buildQuizBackTarget(
      Number(resolvedFlowState.series_id || resolvedFlowState.selectedSeries?.id) > 0 ? QUIZ_ROUTES.testSeriesList : QUIZ_ROUTES.list,
      listState,
    ),
  });
}

function findQuizListBackTarget(flow) {
  if (flow.quizListBackTarget?.pathname) return flow.quizListBackTarget;
  const visited = new Set();
  let target = flow.quizBackTarget;
  while (target?.pathname && !visited.has(target)) {
    visited.add(target);
    if (![QUIZ_ROUTES.start, QUIZ_ROUTES.selection, QUIZ_ROUTES.instruction, QUIZ_ROUTES.detail,
      QUIZ_ROUTES.answerSheet, QUIZ_ROUTES.leaderboard, QUIZ_ROUTES.resultAnalysis].includes(target.pathname)) return target;
    target = target.state?.quizBackTarget;
  }
  return null;
}

export function resolveQuizBackTarget(flow, currentPath, fallback) {
  const visited = new Set();
  let target = flow?.quizBackTarget;
  const invalid = path => path === currentPath ||
    (currentPath === QUIZ_ROUTES.list && Object.values(QUIZ_ROUTES).includes(path)) ||
    (currentPath === QUIZ_ROUTES.testSeriesList && path !== QUIZ_ROUTES.list && Object.values(QUIZ_ROUTES).includes(path));
  while (target?.pathname && invalid(target.pathname) && !visited.has(target)) {
    visited.add(target);
    target = target.state?.quizBackTarget;
  }
  return target?.pathname && !invalid(target.pathname) ? target : fallback;
}

export function navigateQuizBack(navigate, target) {
  const index = typeof window !== 'undefined' ? window.history.state?.idx : null;
  if (Number.isInteger(index) && Number.isInteger(target.historyIndex) && target.historyIndex < index) {
    navigate(target.historyIndex - index);
  } else {
    navigate(target.pathname, { state: target.state, replace: true });
  }
}

